import { downloadZip } from "client-zip"
import Papa from 'papaparse'
import PDFPrinter from './pdfPrinter'
import slugify from "@sindresorhus/slugify"

/**
 * @function slugifyList
 * 
 * @param name
 * 
 * Returns a slugged name with a date part for this list
 * 
 **/ 
const slugifyList = (name) => {
  return `${ slugify(name) }-${ slugify(new Date().toDateString()) }`
}
/**
 * @function downloadBlobUrl
 * 
 * Downloads a blob via hidden anchor
 * 
 **/ 
const downloadBlobUrl = (blobUrl, filename) => {
  const anchor = document.createElement('a')
  anchor.download = filename
  anchor.href = blobUrl

  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  if (typeof blobUrl !== 'string') blobUrl?.revokeObjectURL()
}

/**
 * @class DataPrinter
 * 
 * Functions for saving data as CSV and downloading it.
 * 
 **/
class DataPrinter {
  pdfPrinter = new PDFPrinter()

  /**
   * @function flattenRow
   * 
   * @param {Object} row
   * 
   * Flattens nested properties in `row`
   * 
   **/
  flattenRow(row) {
    let result = { ...row }

    // Flatten nested structures
    // - titles
    if ('alternate-title' in (result.titles ?? {})) {
      result.alternateTitle = result.titles['alternate-title'].titles.map((title) => `${title.title} (${title.notes})`).join(", ")
    }

    if ('title-1999-catalogue-raisonne' in (result.titles ?? {})) {
      result.cr1999Title = result.titles['title-1999-catalogue-raisonne'].titles.map((title) => `${title.title} (${title.notes})`).join(", ")
    }

    if ('alternate-title-1999-catalogue-raisonne' in (result.titles ?? {})) {
      result.alternateCr1999Titles = result.titles['alternate-title-1999-catalogue-raisonne'].titles.map((title) => `${title.title} (${title.notes})`).join(", ")
    }

    delete result.titles

    // - inscriptions
    let inscriptions = {}
    Object.entries(result.inscriptions ?? {}).forEach(([key, value]) => {
      inscriptions[`${key}-inscriptions`] = value.join(", ")
    })

    delete result.inscriptions
    result = { ...result, ...inscriptions }

    // - remarks
    const remarks = (result.remarks ?? []).join(", ")
    result.remarks = remarks

    // - materials
    let crMaterials = result.crMaterials ?? []
    crMaterials.forEach((material) => {
      result[`${material.type}-description`] = material.content
    })
    delete result.crMaterials

    result.exhibitions = (result.cr_data?.exhibitions ?? []).map((exhibition) => exhibition._id)

    // - references and provenance stay as-is

    // - conservation
    if ((result.conservation ?? []).length > 0) {
      let conservations = {}
      result.conservation.forEach((treatment, index) => {
        let label = ''
        const { report_notes, condition_narrative, date, reporter } = treatment

        if (reporter && date) label += `Reported by ${reporter.name} on ${date}\n`
        if (report_notes) label += `Report Notes: ${report_notes}\n`
        if (condition_narrative) label += `Condition Narrative: ${condition_narrative}\n`

        conservations[`conservation-${index}`] = `${treatment.condition_narrative}`
      })

      result = { ...result, ...conservations }
      delete result.conservation
    }

    // Add a link and remove any remaining internal-only data
    result.link = new URL(result.id, window.location.origin).href
    delete result.imageUrl
    delete result.list

    return result
  } 
  /**
   * @function prepareRow
   * 
   * @param {Object} row
   * 
   * Renames columns, etc
   * 
   * TODO: Do this more ergonomically than clone-and-mutate
   * 
   **/ 
  prepareRow(row, dataOptions) {
    const aboutData = window.structuredClone(row.cr_data)

    let result = { ...row }
    delete result.cr_data
    delete result._updated_date

    result.dateAdded = result.createdDate
    delete result.createdDate

    result.imageFilename = ''
    if (result.imageUrl !== '' && result.imageDownload) {
      const imageNumber = result.imageUrl.split('/').at(-1)

      result.imageFilename = `${result.filename_slug}_${imageNumber}.jpg`
    }
    delete result.imageDownload

    if (aboutData?.cr_number) {
      result.cr_number = aboutData.cr_number
    }

    // Insert data according to options
    if (dataOptions.titleDate && aboutData?.titles) {
      result.titles = aboutData.titles
    }

    if (dataOptions.remarks && aboutData?.inscriptions) {
      result.inscriptions = aboutData.inscriptions
    }

    if (dataOptions.remarks && aboutData?.remarks_1999) {
      result.remarks = aboutData.remarks_1999
    }

    if (dataOptions.materials && aboutData?.materials) {
      result.crMaterials = aboutData.materials
    }

    if (dataOptions.exhibitions && aboutData?.exhibitions) {
      result.exhibitions = aboutData.exhibitions
    }

    if (dataOptions.references && aboutData?.references) {
      result.references = aboutData.references
    }

    if (dataOptions.provenance && aboutData?.provenance) {
      result.provenance = aboutData.provenance
    }

    if (dataOptions.conservation && aboutData?.conservation) {
      result.conservation = aboutData.conservation
    }

    return result 
  }

  /**
   * @function prepare
   * 
   * @param {Object} data
   * 
   * Filters and renames columns of rows in `data`, other normalizations for export
   * 
   * TODO: `row` needs filename data if user exported images
   * 
   **/ 
  prepare(data, options) {
    // Normalize rows and rename / string-format properties as appropriate
    const result = data.map((row) => this.prepareRow(row, options.data))

    if (options.format === 'CSV' || options.format === 'CSV-BOM') {
      return result.map(this.flattenRow)
    }

    return result
  }

  /**
   * @function formatImageUrl
   * 
   * @param {string} iiifUrl
   * @param {Number} width
   * 
   * Formats an image URL with the specified width. 
   * 
   * @returns {String}
   * @throws When `iiifUrl` is not valid.
   * 
   **/
  formatImageUrl(iiifUrl, width) {
    const urlStem = iiifUrl.endsWith('/') ? iiifUrl : iiifUrl + '/'
    const pathParams = `full/${width},/0/default.jpg`
    const url = new URL(pathParams, urlStem)

    return url.href
  }

  /**
   * @function downloadImages
   * 
   * @param {Array<Object>} data
   * 
   * Download data for each image from an object in `data`
   * 
   * @return Array of result objects useable by `client-zip`
   * 
   **/ 
  async downloadImages(data, imageOptions) {

    const results = []
    for (const row of data) {
      const { filename_slug, imageDownload, imageUrl } = row

      if (!imageUrl || !imageDownload) continue

      const width = imageOptions.size === 'small' ? '240' : '1024'

      const response = await fetch(this.formatImageUrl(row.imageUrl, width))
      const blob = await response.blob()

      // Prepend the entity filename slug
      const name = `${filename_slug}_${row.imageUrl.split('/').at(-1)}.jpg`
      results.push({ name, input: blob, lastModified: new Date() })
    }

    return results
  }

  /**
   * @function format
   * 
   * @param {Array<Object>} data
   * @param {Object} options
   * 
   * Formats data according to `options` and returns the resulting blob
   * 
   **/ 
  async format(name, description, created, updated, data, options) {
    const prepared = this.prepare(data, options)

    switch (options.format) {
      case 'CSV':
      case 'CSV-BOM':
        const headers = new Set()
        for (const item of prepared) {
          Object.keys(item).forEach((key) => headers.add(key))
        }

        let csv = Papa.unparse(prepared, { columns: Array.from(headers) })
        if (options.format === 'CSV-BOM') csv = '\uFEFF'+ csv

        return new Blob([csv], { type: 'text/csv' })

      case 'JSON':
        // TODO: add list data (id, name, description, etc)
        const json = JSON.stringify(prepared)
        return new Blob([json], { type: 'application/json' })

      case 'PDF':
        const printer = new PDFPrinter()

        const buffer = await printer.print(name, description, created, updated, data, options)

        return new Blob([buffer], { type: 'application/pdf' })
    }
  }

  /**
   * @function print
   * 
   * @param {String} name
   * @param {Array<Object>} data
   * 
   * Downloads `data` to a file using a URL safe slug of `name` as the filename
   * 
   **/ 
  async print(name, description, created, updated, data, options) {
    // Serialize the data
    const blob = await this.format(name, description, created, updated, data, options)
    const filenameSlug = slugifyList(name)
    let filename
    switch(options.format) {
      case 'CSV':
      case 'CSV-BOM':
        filename = `${filenameSlug}.csv`
        break
      case 'JSON':
        filename = `${filenameSlug}.json`
        break
      case 'PDF':
        filename = `${filenameSlug}.pdf`
        break
    }

    if (options.images.size) {
      const images = await this.downloadImages(data, options.images)
      const zip = await downloadZip([{ name: filename, input: blob }, ...images]).blob()
      const zipName = `${filenameSlug}.zip`

      const blobUrl = window.URL.createObjectURL(zip)
      downloadBlobUrl(blobUrl, zipName)
    } else {
      const blobUrl = window.URL.createObjectURL(blob)
      downloadBlobUrl(blobUrl, filename)      
    }
  }
}

export { DataPrinter, PDFPrinter }